import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import type { ApiClient, Bauteilbefund } from '../../api';
import type { Rahmen } from '../Projektrahmen';
import { IconOk } from '../../Icons';
import { PageHeader } from '../../rahmen/PageHeader';
import { elementkennung } from '../../elementkennung';
import {
  DataTable, EmptyState, ErrorState, Feld, KpiCard, KpiReihe, LoadingState, StatusBadge, Vorbehalte, formatiereZahl,
  spalteFuer, type Spalte,
} from '../../muster';
import { spanne } from './datenregeln';
import './lueftung.css';

type TypBefund = Bauteilbefund['jeTyp'][number];
type Bauteil = Bauteilbefund['bauteile'][number];

/** So viele Bauteile liefert der Server höchstens; wer mehr braucht, grenzt über den Typ ein. */
const SERVERGRENZE = 1000;

/**
 * Bauteile mit Befund aus dem Datenblattabgleich.
 *
 * Ein Befund entsteht, wenn die geplante Luftmenge außerhalb des Bereichs der
 * Herstellertabelle liegt. Das ist ein Planungsbefund, kein Datenproblem:
 * Entweder ist die Baugröße zu klein gewählt oder die Luftmenge falsch.
 *
 * Nach Typ gruppiert, weil ein Befund fast immer den ganzen Typ betrifft - ist
 * die Baugröße zu klein, dann für alle Bauteile mit dieser Luftmenge. Wer das
 * einzeln abarbeitet, verliert den Blick auf die Ursache.
 *
 * TV6d (TX-STANDARD 1.6): nach `seiten/lueftung/` umgezogen (Route bleibt
 * `lueftung/befunde`), ohne Inline-Stil; die Grenze des Servers steht als
 * Vorbehalt unter dem Kopf. Die Aufrufe sind unverändert
 * (`Lueftungsdaten.schreibwege.test.tsx`).
 */
export function Bauteilbefunde() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const { id } = useParams<{ id: string }>();

  const [befunde, setBefunde] = useState<Bauteilbefund | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [nurOffen, setNurOffen] = useState(true);

  const laden = useCallback(async () => {
    try {
      setBefunde(await api.bauteilbefunde(projekt.id, nurOffen ? 'offen' : undefined));
      setFehler(null);
    } catch (e) {
      setFehler(e);
    }
  }, [api, projekt.id, nurOffen]);

  useEffect(() => { void laden(); }, [laden]);

  const typSpalten: Spalte<TypBefund>[] = useMemo(() => [
    { schluessel: 'anzahl', titel: 'Bauteile', art: 'zahl', nachkomma: 0, breite: '100px', wert: (t) => t.anzahl },
    {
      schluessel: 'typ', titel: 'Typ', immerSichtbar: true, wert: (t) => t.typ,
      inhalt: (t) => <><span>{t.typ}</span><span className="lue-zweitzeile">{t.familie}</span></>,
    },
    {
      // Sortiert wird nach der Untergrenze - die entscheidet, ob die Baugröße zu klein gewählt ist.
      schluessel: 'luftmengen', titel: 'Luftmengen', einheit: 'm³/h', ausrichtung: 'rechts', breite: '160px',
      wert: (t) => t.von, inhalt: (t) => spanne(t.von, t.bis),
    },
    { schluessel: 'hinweis', titel: 'Befund', wert: (t) => t.hinweis },
  ], []);

  const bauteilSpalten: Spalte<Bauteil>[] = useMemo(() => [
    {
      // Nicht slice(0, 8): Das sind die ersten acht Zeichen der Revit-UniqueId, also der Dokumentanteil - für jedes
      // Bauteil desselben Modells derselbe (Befund 18.09.2026).
      schluessel: 'kennung', titel: 'Kennung', immerSichtbar: true,
      wert: (b) => b.aksSchluessel ?? elementkennung(b.elementGuid),
      inhalt: (b) => <span title={b.elementGuid}>{b.aksSchluessel ?? elementkennung(b.elementGuid)}</span>,
    },
    {
      schluessel: 'raum', titel: 'Raum', wert: (b) => b.raumnummer ?? '',
      inhalt: (b) => <><span>{b.raumnummer ?? ''}</span>{b.raumname && <span className="lue-zweitzeile">{b.raumname}</span>}</>,
    },
    { schluessel: 'ebene', titel: 'Ebene', wert: (b) => b.ebene },
    { schluessel: 'volumenstrom', titel: 'Luftmenge', ...spalteFuer('volumenstrom_luft'), breite: '130px', wert: (b) => b.volumenstrom },
    {
      schluessel: 'zustand', titel: 'Zustand', art: 'status', breite: '130px',
      wert: (b) => (b.fehlerZustand === 'geprueft' ? 'angesehen' : 'offen'),
      inhalt: (b) => (b.fehlerZustand === 'geprueft'
        ? <StatusBadge zustand="neutral" text="angesehen" klein />
        : <StatusBadge zustand="warnung" text="offen" klein />),
    },
    {
      schluessel: 'aktion', titel: '', breite: '120px', sortierbar: false, ausrichtung: 'rechts',
      inhalt: (b) => <Angesehen bauteil={b} api={api} neuLaden={laden} />,
    },
  ], [api, laden]);

  const kopf = (
    <PageHeader
      titel="Befunde aus dem Datenblattabgleich"
      beschreibung={'Die geplante Luftmenge liegt außerhalb des Bereichs, den die Herstellertabelle abdeckt. Das Produkt '
        + 'ist zugeordnet, die luftmengenabhängigen Werte nicht - sie ließen sich nur durch Extrapolation gewinnen, und '
        + 'das wäre eine Erfindung.'}
      meta={<Link to={`/projekte/${id}/lueftung`}>← Lüftungsbestand</Link>}
    />
  );

  if (fehler !== null && !befunde) {
    return (
      <>
        {kopf}
        <ErrorState meldung="Die Befunde konnten nicht geladen werden." technisch={fehler} beiErneutVersuchen={() => void laden()} />
      </>
    );
  }

  if (!befunde) {
    return (
      <>
        {kopf}
        <LoadingState text="Befunde werden geladen …" zeilen={6} spalten={5} />
      </>
    );
  }

  const gekappt = befunde.bauteile.length >= SERVERGRENZE;

  return (
    <>
      {kopf}

      <Vorbehalte eintraege={gekappt ? [{
        art: 'sonstiges',
        text: `Der Server liefert höchstens ${formatiereZahl(SERVERGRENZE, 0)} Bauteile; die Liste „Einzelne Bauteile“ ist `
          + 'nicht vollständig. Wer mehr braucht, grenzt über den Typ ein.',
      }] : []}
      />

      {befunde.gesamt === 0 ? (
        <EmptyState
          icon={<IconOk />}
          titel="Kein Bauteil trägt einen Befund."
          erklaerung="Alle geplanten Luftmengen liegen im Bereich der jeweiligen Herstellertabelle."
        />
      ) : (
        <KpiReihe>
          {/* Bewertet: außerhalb des Bereichs der Herstellertabelle ist die Grenze. */}
          <KpiCard titel="Bauteile mit Befund" wert={befunde.gesamt} zustand="warnung" zustandText="außerhalb der Herstellertabelle" />
          <KpiCard titel="Davon offen" wert={befunde.offen} />
          <KpiCard titel="Betroffene Typen" wert={befunde.jeTyp.length} />
        </KpiReihe>
      )}

      {/* Nach Typ: Hier steht die Ursache, nicht am einzelnen Bauteil. */}
      {befunde.jeTyp.length > 0 && (
        <DataTable
          name="Nach Bauteiltyp"
          beschreibung="Hier steht die Ursache - ein Befund betrifft fast immer den ganzen Typ."
          spalten={typSpalten}
          zeilen={befunde.jeTyp}
          schluessel={(t) => `${t.familie}|${t.typ}`}
          sortierung={{ spalte: 'anzahl', richtung: 'ab' }}
          ausfuhr
          ausfuhrDateiname="befunde-je-typ"
        />
      )}

      {befunde.gesamt > 0 && (
        <DataTable
          name="Einzelne Bauteile"
          spalten={bauteilSpalten}
          zeilen={befunde.bauteile}
          schluessel={(b) => b.id}
          ausfuhr
          ausfuhrDateiname="befunde-bauteile"
          suchePlatzhalter="Kennung, Raum, Ebene …"
          trefferText={gekappt ? `${formatiereZahl(befunde.bauteile.length, 0)} von ${formatiereZahl(befunde.gesamt, 0)} Bauteilen` : undefined}
          werkzeuge={(
            <Feld art="haken" beschriftung="Nur offene Befunde" wert={nurOffen} beiAenderung={(w) => setNurOffen(w as boolean)} />
          )}
          leer={(
            <EmptyState
              titel={nurOffen ? 'Kein offener Befund.' : 'Kein Bauteil mit Befund.'}
              erklaerung={nurOffen
                ? 'Alle Befunde sind angesehen. Der Haken oben zeigt auch die erledigten.'
                : 'Die geplanten Luftmengen liegen im Bereich der Herstellertabellen.'}
              aktion={nurOffen ? <button type="button" className="m-knopf" onClick={() => setNurOffen(false)}>Auch angesehene zeigen</button> : undefined}
            />
          )}
        />
      )}
    </>
  );
}

function Angesehen({ bauteil: b, api, neuLaden }: { bauteil: Bauteil; api: ApiClient; neuLaden: () => void }) {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  if (fehler) return <StatusBadge zustand="fehler" text="Fehler" titel={fehler} klein />;
  if (b.fehlerZustand === 'geprueft') return null;

  return (
    <button
      type="button"
      className="m-knopf"
      disabled={laeuft}
      onClick={async (e) => {
        e.stopPropagation();
        setLaeuft(true);
        setFehler(null);
        try {
          await api.befundGeprueft(b.id);
          neuLaden();
        } catch (ex) {
          setFehler(ex instanceof Error ? ex.message : String(ex));
        } finally {
          setLaeuft(false);
        }
      }}
    >
      {laeuft ? '…' : 'angesehen'}
    </button>
  );
}
